import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Typography } from '../../../atoms/typography/typography';
import {
  LpCardList,
  LpSelected,
  StyledDiv,
  TitleText,
  Walkthrough,
} from '@taleemabad/dsm/components';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import { DownloadStatus, ILessonPlan } from '@taleemabad/db';
import { ChipFilterWithContainer } from '../../../atoms/chip/chip-filter';
import { Capacitor } from '@capacitor/core';
import { DriveStep } from 'driver.js';

interface LessonPlanChapterTopicsProps {
  topicName: string;
  topicIndex: number;
  className: string;
  schoolClass: number;
  lessonPlans: ILessonPlan[];
  chapterId: number;
  lpDownloadEnabled?: boolean;
  lpDownloadAbTestEnable?: boolean;
  loadingComponent: () => React.ReactNode;
  onSingleLpSelected?: (data: LpSelected) => void;
  onLpDelete?: (lesson: ILessonPlan) => void;
  onLpDownload?: (lesson: LpSelected) => void;
  onRetryDownloadLp?: (lesson: ILessonPlan) => void;
  onCancelLp?: (lesson: ILessonPlan) => void;
  walkthroughStep: DriveStep;
  walkthroughEnabled: boolean;
  lpDownloadWalkthroughFeatureFlagEnabled: boolean;
}

const TitleTextContainer = styled.div`
  margin: var(--rk-spacing-xl) 0 var(--rk-spacing-xxl) 0;
  ${mq(BreakpointSizes.xs)} {
    margin: var(--rk-spacing-md) 0 var(--rk-spacing-xxl) 0;
  }
`;
const LpType = [
  {
    id: 1,
    name: 'All',
  },
  {
    id: 2,
    name: 'Downloads',
  },
];
export const LessonPlanChapterTopics: React.FC<
  LessonPlanChapterTopicsProps
> = ({
  topicName,
  topicIndex,
  className,
  lessonPlans,
  chapterId,
  schoolClass,
  lpDownloadEnabled = false,
  lpDownloadAbTestEnable = false,
  loadingComponent,
  onSingleLpSelected,
  onLpDelete,
  onLpDownload,
  onRetryDownloadLp,
  onCancelLp,
  walkthroughStep,
  walkthroughEnabled,
  lpDownloadWalkthroughFeatureFlagEnabled,
}) => {
  const [selectedLpType, setSelectedLpType] = useState(1);
  useState<ILessonPlan | null>(null);
  const [selectedLessonPlanList, setSelectedLessonPlanList] =
    useState<ILessonPlan[]>(lessonPlans);

  useEffect(() => {
    const lessonPlanList =
      selectedLpType === 2
        ? [...lessonPlans]
            .filter(
              (lesson) => lesson.downloadStatus === DownloadStatus.Completed,
            )
            .sort((a, b) =>
              a.downloadStatus === DownloadStatus.Completed &&
              b.downloadStatus !== DownloadStatus.Completed
                ? -1
                : b.downloadStatus === DownloadStatus.Completed &&
                    a.downloadStatus !== DownloadStatus.Completed
                  ? 1
                  : 0,
            )
        : lessonPlans;
    setSelectedLessonPlanList(lessonPlanList);
  }, [selectedLpType, lessonPlans]);

  return (
    <StyledDiv padding="var(--rk-spacing-xl)" flexDirection="column">
      <Typography variant="textHeadlineMediumPrimary">{topicName}</Typography>
      <TitleTextContainer>
        <TitleText
          leftText={`Topic ${topicIndex}`}
          seperatorIconName="circle"
          rightText={className}
        ></TitleText>
      </TitleTextContainer>
      {Capacitor.isNativePlatform() && lpDownloadEnabled && (
        <StyledDiv
          gap={10}
          marginTop={16}
          marginBottom={16}
          margin="0 0 var(--rk-spacing-xl) 0"
        >
          {LpType.map((item) => (
            <ChipFilterWithContainer
              selected={selectedLpType === item.id}
              onClick={() => {
                setSelectedLpType(item.id);
              }}
              key={item.name}
              label={item.name}
            />
          ))}
        </StyledDiv>
      )}
      <LpCardList
        onLpDelete={(lessonPlan) => {
          onLpDelete?.(lessonPlan);
        }}
        schoolClass={schoolClass}
        onLpSelected={(data) => {
          onSingleLpSelected?.(data);
        }}
        lessonPlans={selectedLessonPlanList}
        onTrailingIconClick={onLpDownload}
        lpDownloadEnabled={lpDownloadEnabled}
        onRetryDownloadLp={(data) => {
          onRetryDownloadLp?.(data);
        }}
        onCancelLp={(data) => {
          onCancelLp?.(data);
        }}
        lpDownloadAbTestEnable={lpDownloadAbTestEnable}
        chapterId={chapterId}
      />
      {loadingComponent()}

      {selectedLessonPlanList.length === 0 &&
        selectedLpType === 2 &&
        lpDownloadEnabled && (
          <StyledNoDownloadLp>
            <Typography variant="textTitleMediumNeutral10">
              Your downloaded lessons <br />
              will appear here
            </Typography>
          </StyledNoDownloadLp>
        )}
      {lpDownloadWalkthroughFeatureFlagEnabled &&
        walkthroughEnabled &&
        Capacitor.isNativePlatform() && (
          <Walkthrough
            walkthroughSteps={walkthroughStep}
            showWalkthrough={walkthroughEnabled}
            positionProps={{
              side: walkthroughStep.popover?.side,
              align: walkthroughStep.popover?.align,
            }}
          />
        )}
    </StyledDiv>
  );
};

const StyledNoDownloadLp = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100vh - 240px);
  ${mq(BreakpointSizes.xs)} {
    height: calc(100vh - 260px);
  }
`;
